import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import api from '../api/axios';
import { getErrorMessage } from '../api/errorMessage';
import { fetchMatchCounts } from '../api/matches';
import { MatchEventMessage, MatchEventType, useMatchEvents } from '../api/matchEvents';
import { Match, MatchCounts, MatchPage } from '../types';
import { useCatalog } from '../catalog';
import { resolveHeroDisplayName } from '../utils/dotaNames';
import { truncateNick } from '../utils/truncateNick';
import { tr } from '../i18n';
import { createPortal } from 'react-dom';
import HeroIcon from '../ui/hero-icon/HeroIcon';
import PlayerAvatar from '../ui/avatar/PlayerAvatar';
import { EmptyState } from '../ui/system-states';
import { TableSkeleton } from '../ui/TableSkeleton';
import { useAppToast } from '../ui/toast';
import './MatchesPage.css';

// save-state активных матчей шлёт UPDATED ~раз в секунду — чаще раза в минуту BE не дёргаем
const NEW_MATCHES_DEBOUNCE_MS = 60_000;

// ─── helpers ─────────────────────────────────────────────────────────────────

const pad2 = (n: number): string => String(n).padStart(2, '0');

const formatShortDate = (value?: string): string => {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  const day = pad2(d.getDate());
  const month = pad2(d.getMonth() + 1);
  const year = d.getFullYear();
  const hours = pad2(d.getHours());
  const minutes = pad2(d.getMinutes());
  return `${day}.${month}.${year} ${hours}:${minutes}`;
};

const formatElapsed = (ms: number): string => {
  const totalMin = Math.round(ms / 60_000);
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  return h > 0 ? tr.matches.list.hoursMinutes(h, m) : tr.matches.list.minutes(m);
};

const formatDuration = (start?: string, end?: string): string => {
  if (!start) return '—';
  const startMs = new Date(start).getTime();
  if (Number.isNaN(startMs)) return '—';
  if (!end) {
    const elapsed = Date.now() - startMs;
    if (elapsed < 0) return tr.matches.list.live;
    return `${formatElapsed(elapsed)}`;
  }
  const ms = new Date(end).getTime() - startMs;
  if (Number.isNaN(ms) || ms < 0) return '—';
  return formatElapsed(ms);
};

const formatMatchType = (code?: string): string => {
  if (!code) return '—';
  return code.replace('PVP_', '').charAt(0) + code.replace('PVP_', '').slice(1).toLowerCase();
};

const modeClass = (code?: string): string => {
  if (code === 'PVP_SOLO') return ' mp-type-pill--solo';
  if (code === 'PVP_DUO') return ' mp-type-pill--duo';
  return '';
};

const toStartOfLocalDayIso = (value?: string) => {
  if (!value) return null;
  const [year, month, day] = value.split('-').map(Number);
  if (!year || !month || !day) return null;
  return new Date(year, month - 1, day, 0, 0, 0, 0).toISOString();
};

const toEndOfLocalDayIso = (value?: string) => {
  if (!value) return null;
  const [year, month, day] = value.split('-').map(Number);
  if (!year || !month || !day) return null;
  return new Date(year, month - 1, day, 23, 59, 59, 999).toISOString();
};

const getDateFrom = (preset: string): string | null => {
  const now = new Date();
  if (preset === 'today') {
    return new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0).toISOString();
  }
  if (preset === '7d') {
    const d = new Date(now);
    d.setDate(d.getDate() - 7);
    return d.toISOString();
  }
  if (preset === '30d') {
    const d = new Date(now);
    d.setDate(d.getDate() - 30);
    return d.toISOString();
  }
  return null;
};

const getLocalTodayRange = (): { from: string; to: string } => {
  const now = new Date();
  const from = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0);
  const to = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
  return { from: from.toISOString(), to: to.toISOString() };
};

// ─── types ────────────────────────────────────────────────────────────────────

const SORT_OPTIONS = [
  { value: 'startDateTime:desc', label: tr.matches.list.sort.newest },
  { value: 'startDateTime:asc', label: tr.matches.list.sort.oldest },
  { value: 'round:desc', label: tr.matches.list.sort.mostRounds },
  { value: 'round:asc', label: tr.matches.list.sort.fewestRounds }
] as const;

type MatchSort = (typeof SORT_OPTIONS)[number]['value'];

const DEFAULT_SORT: MatchSort = 'startDateTime:desc';

type FiltersState = {
  search: string;
  status: '' | 'true' | 'false';
  matchType: string;
  datePreset: '' | 'today' | '7d' | '30d';
  onlyMy: boolean;
  onlyActive: boolean;
  /** Чип «Завершённые сегодня»: фильтр по дате ЗАВЕРШЕНИЯ, а не старта. */
  endedToday: boolean;
  sort: MatchSort;
};

type MatchesPageProps = {
  selfUid?: string | null;
};

// ─── sub-components ──────────────────────────────────────────────────────────

type RosterPlayer = NonNullable<Match['players']>[number];

const Dash: React.FC = () => <span className="mp-cell-dash">—</span>;

const FocusHeroCell: React.FC<{ player?: RosterPlayer }> = ({ player }) => {
  const catalog = useCatalog();
  if (!player?.heroName) return <Dash />;
  return (
    <span className="mp-hero-cell">
      <HeroIcon heroName={player.heroName} />
      <span className="mp-hero-cell-name">{resolveHeroDisplayName(catalog, player.heroName)}</span>
      {player.place != null && (
        <span className={`mp-hero-cell-place${player.place === 1 ? ' first' : ''}`}>#{player.place}</span>
      )}
    </span>
  );
};

const WinnerCell: React.FC<{ players: Match['players'] }> = ({ players = [] }) => {
  // в Duo первое место делит вся команда
  const winners = players.filter(p => p.place === 1);
  if (winners.length === 0) return <Dash />;
  return (
    <span className="mp-winners">
      {winners.map(winner => (
        <span key={winner.uid} className="mp-winner">
          <HeroIcon heroName={winner.heroName} />
          <span className="mp-winner-name">{truncateNick(winner.name || winner.uid)}</span>
        </span>
      ))}
    </span>
  );
};

// Аватары ничего не говорили: показываем число игроков и победителя, состав — в подсказке.
const byPlaceThenName = (a: RosterPlayer, b: RosterPlayer): number => {
  const placeA = a.place ?? Number.MAX_SAFE_INTEGER;
  const placeB = b.place ?? Number.MAX_SAFE_INTEGER;
  if (placeA !== placeB) return placeA - placeB;
  return (a.name || a.uid).localeCompare(b.name || b.uid);
};

const ROSTER_POPOVER_WIDTH = 280;
const ROSTER_GAP = 6;

// Строку целиком накрывает ссылка row-link, поэтому триггер поднят над ней, а поповер — в портале (карточка таблицы режет overflow).
const RosterPopover: React.FC<{ players: RosterPlayer[] }> = ({ players }) => {
  const [position, setPosition] = useState<{ top: number; left: number; maxHeight: number } | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const closeTimerRef = useRef<number | null>(null);
  const popoverId = useRef(`mp-roster-${Math.random().toString(36).slice(2)}`).current;

  const cancelClose = () => {
    if (closeTimerRef.current != null) window.clearTimeout(closeTimerRef.current);
    closeTimerRef.current = null;
  };

  const open = () => {
    cancelClose();
    const rect = triggerRef.current?.getBoundingClientRect();
    if (!rect) return;
    const spaceBelow = window.innerHeight - rect.bottom - ROSTER_GAP * 2;
    const spaceAbove = rect.top - ROSTER_GAP * 2;
    const placeBelow = spaceBelow >= 220 || spaceBelow >= spaceAbove;
    const maxHeight = Math.max(160, placeBelow ? spaceBelow : spaceAbove);
    const width = Math.min(ROSTER_POPOVER_WIDTH, window.innerWidth - 32);
    const left = Math.min(Math.max(16, rect.left), window.innerWidth - width - 16);
    const top = placeBelow ? rect.bottom + ROSTER_GAP : Math.max(ROSTER_GAP, rect.top - ROSTER_GAP - Math.min(maxHeight, players.length * 36 + 16));
    setPosition({ top, left, maxHeight });
  };

  const scheduleClose = () => {
    cancelClose();
    closeTimerRef.current = window.setTimeout(() => setPosition(null), 120);
  };

  useEffect(() => {
    if (!position) return;
    const close = () => setPosition(null);
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') close(); };
    // на таче нет mouseleave — закрываем тапом мимо
    const onPointerDown = (e: PointerEvent) => {
      const target = e.target as Node;
      if (triggerRef.current?.contains(target) || document.getElementById(popoverId)?.contains(target)) return;
      close();
    };
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    window.addEventListener('keydown', onKey);
    window.addEventListener('pointerdown', onPointerDown);
    return () => {
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('pointerdown', onPointerDown);
    };
  }, [position, popoverId]);

  useEffect(() => cancelClose, []);

  const sorted = [...players].sort(byPlaceThenName);

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className="mp-players-count"
        aria-expanded={position != null}
        aria-controls={position ? popoverId : undefined}
        onMouseEnter={open}
        onMouseLeave={scheduleClose}
        onFocus={open}
        onBlur={scheduleClose}
        onClick={e => {
          e.preventDefault();
          e.stopPropagation();
          open();
        }}
      >
        {tr.matches.list.playersCount(players.length)}
      </button>
      {position && createPortal(
        <div
          id={popoverId}
          className="mp-roster-popover"
          role="dialog"
          aria-label={tr.matches.list.rosterLabel}
          style={{ top: position.top, left: position.left, maxHeight: position.maxHeight, width: Math.min(ROSTER_POPOVER_WIDTH, window.innerWidth - 32) }}
          onMouseEnter={cancelClose}
          onMouseLeave={scheduleClose}
        >
          <ul className="mp-roster-list">
            {sorted.map(p => (
              <li key={p.uid}>
                <Link to={`/players/${encodeURIComponent(p.uid)}`} className="mp-roster-item">
                  <span className={`mp-roster-place${p.place === 1 ? ' first' : ''}`}>{p.place != null ? `#${p.place}` : '—'}</span>
                  <PlayerAvatar uid={p.uid} displayName={p.name} avatarUrl={p.avatarUrl} size={22} />
                  <span className="mp-roster-name">{truncateNick(p.name || p.uid)}</span>
                  <HeroIcon heroName={p.heroName} />
                </Link>
              </li>
            ))}
          </ul>
        </div>,
        document.body
      )}
    </>
  );
};

const PlayersSummary: React.FC<{ players: Match['players'] }> = ({ players = [] }) =>
  players.length > 0 ? (
    <RosterPopover players={players} />
  ) : (
    <span className="mp-players-count mp-players-count--empty">{tr.matches.list.playersCount(0)}</span>
  );

type RoundCellProps = {
  round?: number | null;
};

const RoundCell: React.FC<RoundCellProps> = ({ round}) => {
  if (round == null) return <span style={{ color: 'var(--text-3)', fontSize: 12 }}>—</span>;
  return (
    <div className="mp-cell-round">
      <span className="mp-round-label">{round}</span>
    </div>
  );
};

const StatusPill: React.FC<{ ended?: boolean }> = ({ ended }) =>
  ended ? (
    <span className="mp-status-pill ended">{tr.matches.list.statusEnded}</span>
  ) : (
    <span className="mp-status-pill active">
      <span className="mp-pulse-dot" />
      {tr.matches.list.statusLive}
    </span>
  );

// ─── main component ───────────────────────────────────────────────────────────

const emptyMatchesPage: MatchPage = {
  content: [],
  page: 0,
  size: 20,
  totalPages: 0,
  totalElements: 0,
  remainingPages: 0
};

const defaultFilters: FiltersState = {
  search: '',
  status: '',
  matchType: '',
  datePreset: '',
  onlyMy: false,
  onlyActive: false,
  endedToday: false,
  sort: DEFAULT_SORT
};

const pickOption = <T extends string>(value: string | null, allowed: readonly T[]): T | '' =>
  allowed.includes(value as T) ? (value as T) : '';

const readFiltersFromUrl = (params: URLSearchParams, onlyMy: boolean): FiltersState => {
  const onlyActive = params.get('active') === '1';
  return {
    search: params.get('q') ?? '',
    status: pickOption(params.get('status'), ['true', 'false'] as const),
    matchType: params.get('type') ?? '',
    datePreset: pickOption(params.get('date'), ['today', '7d', '30d'] as const),
    onlyMy,
    onlyActive,
    endedToday: !onlyActive && params.get('today') === '1',
    sort: pickOption(params.get('sort'), SORT_OPTIONS.map(o => o.value)) || DEFAULT_SORT
  };
};

const MatchesPage: React.FC<MatchesPageProps> = ({ selfUid }) => {
  const [searchParams, setSearchParams] = useSearchParams();

  const initialOnlyMy = searchParams.get('my') === '1' && Boolean(selfUid);

  const initialLockedUid = searchParams.get('playerUid') || '';
  const initialLockedName = searchParams.get('playerName') || '';

  const [matchesPage, setMatchesPage] = useState<MatchPage>(emptyMatchesPage);
  const [loading, setLoading] = useState(false);
  const [newMatchesCount, setNewMatchesCount] = useState(0);
  const { pushToast } = useAppToast();

  const currentPageRef = useRef(0);
  const requestSeqRef = useRef(0);
  const userRequestSeqRef = useRef(0);
  const contentRef = useRef<Match[]>([]);
  useEffect(() => { contentRef.current = matchesPage.content || []; }, [matchesPage.content]);

  // Locked player filter (arrives via ?playerUid — e.g. "Все матчи" from a player card).
  // Precise (by uid); combines with type/date/status filters; cleared by the chip or a
  // manual free-text search.
  const [lockedPlayer, setLockedPlayer] = useState<{ uid: string; name: string } | null>(
    initialLockedUid ? { uid: initialLockedUid, name: initialLockedName || initialLockedUid } : null
  );
  const lockedPlayerRef = useRef(lockedPlayer);
  useEffect(() => { lockedPlayerRef.current = lockedPlayer; }, [lockedPlayer]);

  const [filters, setFilters] = useState<FiltersState>(() => readFiltersFromUrl(searchParams, initialOnlyMy));
  // uid-ы, в которые разрешился текстовый поиск по нику — для колонки «Герой игрока»
  const [searchResolvedUids, setSearchResolvedUids] = useState<string[]>([]);

  // Счётчики чипов — общие тоталы с бэка, не зависят от текущей страницы и прочих фильтров.
  const [counts, setCounts] = useState<MatchCounts>({ active: 0, endedInPeriod: 0, withPlayer: 0 });

  const filtersRef = useRef(filters);

  useEffect(() => {
    filtersRef.current = filters;
  }, [filters]);

  const buildPayload = (
    f: FiltersState,
    page: number,
    resolvedPlayerUids?: string[],
    resolvedMatchId?: string,
  ) => {
    const payload: Record<string, unknown> = { page, size: 20 };

    // onlyActive wins over status select
    if (f.onlyActive) {
      payload.active = true;
    } else if (f.endedToday) {
      payload.active = false;
    } else if (f.status !== '') {
      payload.active = f.status === 'true';
    }

    if (f.endedToday) {
      const today = getLocalTodayRange();
      payload.endDateFrom = today.from;
      payload.endDateTo = today.to;
    }

    // Filter precedence: onlyMy > free-text (players / matchId) > locked player (?playerUid).
    if (f.onlyMy && selfUid) {
      payload.playerUids = [selfUid];
    } else if (resolvedPlayerUids) {
      // free-text matched player names — search by their UIDs
      payload.playerUids = resolvedPlayerUids;
    } else if (resolvedMatchId) {
      // free-text did not match any player but looks like a matchId — try that
      payload.matchIds = [resolvedMatchId];
    } else if (lockedPlayerRef.current) {
      // locked player from ?playerUid (no free-text active) — filter precisely by uid
      payload.playerUids = [lockedPlayerRef.current.uid];
    }

    if (f.sort !== DEFAULT_SORT) {
      const [field, direction] = f.sort.split(':');
      payload.sort = { field, direction };
    }

    if (f.matchType) {
      payload.matchTypeCodes = [f.matchType];
    }

    if (f.datePreset) {
      const from = getDateFrom(f.datePreset);
      if (from) payload.startDateFrom = from;
    }

    return payload;
  };

  const fetchMatches = useCallback(
    async (page = 0, opts: { silent?: boolean } = {}) => {
      const silent = opts.silent ?? false;
      // silent-запрос устаревает, если пока летел — пользователь ушёл на другую страницу
      if (silent && page !== currentPageRef.current) return;

      const seq = ++requestSeqRef.current;
      const isStale = () => seq !== requestSeqRef.current || (silent && page !== currentPageRef.current);
      // свой счётчик для лоадера: silent-запрос, обогнавший ручной, не должен оставлять его висеть
      const userSeq = silent ? userRequestSeqRef.current : ++userRequestSeqRef.current;

      if (!silent) {
        currentPageRef.current = page;
        setLoading(true);
      }
      try {
        const f = filtersRef.current;
        const trimmed = f.search.trim();

        // Free-text resolution strategy (skip when onlyMy is active — it has priority):
        //   1. Always try /api/players/search first — covers nicks with letters AND
        //      nicks that consist only of digits (e.g. "1337").
        //   2. If no players matched and the input is purely numeric, treat it as a matchId.
        //   3. Otherwise — show empty results.
        let resolvedPlayerUids: string[] | undefined;
        let resolvedMatchId: string | undefined;
        if (trimmed && !(f.onlyMy && selfUid)) {
          let playerUids: string[];
          try {
            const playersRes = await api.post<{ content: Array<{ uid: string }> }>(
              '/api/players/search',
              { name: trimmed, size: 50, page: 0 }
            );
            if (isStale()) return;
            playerUids = (playersRes.data?.content || []).map(p => p.uid);
          } catch (err: any) {
            if (silent || isStale()) return;
            pushToast(getErrorMessage(err, tr.matches.list.playerSearchError));
            setMatchesPage(emptyMatchesPage);
            return;
          }

          if (playerUids.length > 0) {
            resolvedPlayerUids = playerUids;
          } else if (/^\d+$/.test(trimmed)) {
            resolvedMatchId = trimmed;
          } else {
            // No players matched and input is not a number — show empty results immediately
            if (!silent && !isStale()) setMatchesPage(emptyMatchesPage);
            return;
          }
        }

        const payload = buildPayload(f, page, resolvedPlayerUids, resolvedMatchId);
        const res = await api.post<MatchPage>('/api/matches/search', payload);
        if (isStale()) return;
        setSearchResolvedUids(resolvedPlayerUids ?? []);
        setMatchesPage(res.data || emptyMatchesPage);
      } catch (err: any) {
        // Тихий перезапрос при ошибке не трогает уже отображённые данные и не показывает тост.
        if (!silent && !isStale()) {
          pushToast(getErrorMessage(err, tr.matches.list.loadError));
          setMatchesPage(emptyMatchesPage);
        }
      } finally {
        if (!silent && userSeq === userRequestSeqRef.current) setLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [pushToast, selfUid]
  );

  // Initial fetch
  useEffect(() => {
    fetchMatches(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadCounts = useCallback(async () => {
    const today = getLocalTodayRange();
    try {
      setCounts(await fetchMatchCounts({ endedFrom: today.from, endedTo: today.to, playerUid: selfUid }));
    } catch {
      // счётчики некритичны: молча оставляем прежние значения, тост не показываем
    }
  }, [selfUid]);

  useEffect(() => {
    loadCounts();
  }, [loadCounts]);

  // ── live-обновление по SSE (см. useMatchEvents) ────────────────
  const pendingEventsRef = useRef<Array<{ matchId: string; type: MatchEventType }>>([]);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const processPendingEvents = useCallback(() => {
    debounceTimerRef.current = null;
    const pending = pendingEventsRef.current;
    pendingEventsRef.current = [];
    if (pending.length === 0) return;

    const page = currentPageRef.current;
    const currentIds = new Set(contentRef.current.map(m => m.id));
    const touchesCurrentPage = pending.some(e => currentIds.has(e.matchId));
    const hasLifecycleEvent = pending.some(
      e => e.type === 'STARTED' || e.type === 'ENDED' || e.type === 'DELETED'
    );

    if (page === 0) {
      if (touchesCurrentPage || hasLifecycleEvent) {
        fetchMatches(0, { silent: true });
      }
    } else if (touchesCurrentPage) {
      fetchMatches(page, { silent: true });
    }

    if (page > 0) {
      const startedCount = pending.filter(e => e.type === 'STARTED').length;
      if (startedCount > 0) {
        setNewMatchesCount(count => count + startedCount);
      }
    }

    if (hasLifecycleEvent) loadCounts();
  }, [fetchMatches, loadCounts]);

  const handleMatchEvent = useCallback(
    (message: MatchEventMessage) => {
      if (message.kind === 'reconnect') {
        // могли пропустить события во время обрыва — тихо перечитываем текущую страницу
        fetchMatches(currentPageRef.current, { silent: true });
        loadCounts();
        return;
      }
      pendingEventsRef.current.push({ matchId: message.matchId, type: message.type });
      // throttle: таймер не переставляем на каждое событие — иначе поток UPDATED никогда его не даст сработать
      if (!debounceTimerRef.current) {
        debounceTimerRef.current = setTimeout(processPendingEvents, NEW_MATCHES_DEBOUNCE_MS);
      }
    },
    [fetchMatches, loadCounts, processPendingEvents]
  );

  useMatchEvents(handleMatchEvent);

  useEffect(() => () => {
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
  }, []);

  const showNewMatches = () => {
    setNewMatchesCount(0);
    fetchMatches(0);
  };

  // Фильтры живут в URL: ссылкой можно поделиться, F5 их не сбрасывает.
  const writeFiltersToUrl = (f: FiltersState) => {
    setSearchParams(prev => {
      const params = new URLSearchParams(prev);
      const set = (key: string, value: string | false) => {
        if (value) params.set(key, value);
        else params.delete(key);
      };
      set('q', f.search.trim());
      set('status', f.status);
      set('type', f.matchType);
      set('date', f.datePreset);
      set('my', f.onlyMy && Boolean(selfUid) && '1');
      set('active', f.onlyActive && '1');
      set('today', f.endedToday && '1');
      set('sort', f.sort !== DEFAULT_SORT && f.sort);
      return params;
    }, { replace: true });
  };

  const dropLockedPlayerParams = () => {
    setSearchParams(prev => {
      const params = new URLSearchParams(prev);
      params.delete('playerUid');
      params.delete('playerName');
      return params;
    }, { replace: true });
  };

  const hasActiveFilters =
    Boolean(lockedPlayer) ||
    filters.search.trim() !== '' ||
    filters.status !== '' ||
    filters.matchType !== '' ||
    filters.datePreset !== '' ||
    filters.onlyMy ||
    filters.onlyActive ||
    filters.endedToday ||
    filters.sort !== DEFAULT_SORT;

  const resetFilters = () => {
    filtersRef.current = defaultFilters;
    setFilters(defaultFilters);
    lockedPlayerRef.current = null;
    setLockedPlayer(null);
    dropLockedPlayerParams();
    writeFiltersToUrl(defaultFilters);
    setNewMatchesCount(0);
    fetchMatches(0);
  };

  const clearLockedPlayer = () => {
    lockedPlayerRef.current = null;
    setLockedPlayer(null);
    dropLockedPlayerParams();
    setNewMatchesCount(0);
    fetchMatches(0);
  };

  const applyFilter = (patch: Partial<FiltersState>) => {
    const next = { ...filtersRef.current, ...patch };
    filtersRef.current = next;
    setFilters(next);
    // A manual free-text search or the "only my" toggle supersedes ?playerUid.
    const supersedesLock = (typeof patch.search === 'string' && patch.search.trim() !== '') || patch.onlyMy === true;
    if (supersedesLock && lockedPlayerRef.current) {
      lockedPlayerRef.current = null;
      setLockedPlayer(null);
      dropLockedPlayerParams();
    }
    writeFiltersToUrl(next);
    setNewMatchesCount(0);
    fetchMatches(0);
  };

  const goToPage = (page: number) => {
    setNewMatchesCount(0);
    fetchMatches(page);
  };

  const content = matchesPage.content || [];

  const onlyMyActive = filters.onlyMy && Boolean(selfUid);
  // все строки и так «твои»/чужого игрока — подсветка своих строк тогда только шумит
  const playerFilterActive = Boolean(lockedPlayer) || onlyMyActive || searchResolvedUids.length > 0;
  const otherFocusUids = lockedPlayer
    ? [lockedPlayer.uid].filter(uid => uid !== selfUid)
    : onlyMyActive
      ? []
      : searchResolvedUids.filter(uid => uid !== selfUid);
  const otherFocusHeader = lockedPlayer ? tr.matches.list.heroOfPlayer(truncateNick(lockedPlayer.name, 16)) : tr.matches.list.columns.playerHero;
  const showSelfColumn = Boolean(selfUid);
  const showOtherColumn = otherFocusUids.length > 0;
  const skeletonColumns = [
    '8%', '7%', '15%',
    ...(showSelfColumn ? ['14%'] : []),
    ...(showOtherColumn ? ['14%'] : []),
    '10%', '14%', '7%', '10%', '5%'
  ];
  const columnCount = skeletonColumns.length;

  const { page, totalPages, totalElements } = matchesPage;

  return (
    <div className="mp-page">
      {/* ── Header ──────────────────────────────────────────── */}
      <div className="mp-header">
        <h1 className="mp-title">{tr.matches.list.title}</h1>
      </div>

      {/* ── Filter row ──────────────────────────────────────── */}
      <div className="mp-filter-row">
        <input
          type="text"
          className="mp-search-input"
          placeholder={tr.matches.list.searchPlaceholder}
          value={filters.search}
          onChange={e => setFilters(f => ({ ...f, search: e.target.value }))}
          onKeyDown={e => { if (e.key === 'Enter') applyFilter({ search: filters.search }); }}
        />

        <select
          className="mp-select"
          value={filters.status}
          onChange={e => applyFilter({ status: e.target.value as FiltersState['status'], onlyActive: false, endedToday: false })}
        >
          <option value="">{tr.matches.list.filters.statusAll}</option>
          <option value="false">{tr.matches.list.filters.statusEnded}</option>
          <option value="true">{tr.matches.list.filters.statusActive}</option>
        </select>

        <select
          className="mp-select"
          value={filters.matchType}
          onChange={e => applyFilter({ matchType: e.target.value })}
        >
          <option value="">{tr.matches.list.filters.typeAll}</option>
          <option value="PVP_SOLO">Solo</option>
          <option value="PVP_DUO">Duo</option>
        </select>

        <select
          className="mp-select"
          value={filters.datePreset}
          onChange={e => applyFilter({ datePreset: e.target.value as FiltersState['datePreset'], endedToday: false })}
        >
          <option value="">{tr.matches.list.filters.startAll}</option>
          <option value="today">{tr.matches.list.filters.today}</option>
          <option value="7d">{tr.matches.list.filters.days7}</option>
          <option value="30d">{tr.matches.list.filters.days30}</option>
        </select>

        <select
          className="mp-select"
          value={filters.sort}
          onChange={e => applyFilter({ sort: e.target.value as MatchSort })}
          aria-label={tr.matches.list.sortLabel}
        >
          {SORT_OPTIONS.map(o => (
            <option key={o.value} value={o.value}>{tr.matches.list.sortOption(o.label)}</option>
          ))}
        </select>
      </div>

      {/* ── Chip filters ────────────────────────────────────── */}
      <div className="mp-chips">
        {lockedPlayer && (
          <button
            type="button"
            className="mp-chip active mp-chip--locked"
            onClick={clearLockedPlayer}
            title={tr.matches.list.clearPlayerFilter}
          >
            {tr.matches.list.playerChip(lockedPlayer.name)}
            <span className="mp-chip-x" aria-hidden="true">✕</span>
          </button>
        )}

        <button
          type="button"
          className={`mp-chip${filters.onlyActive && !filters.status ? ' active' : ''}`}
          aria-pressed={filters.onlyActive && !filters.status}
          onClick={() =>
            filters.onlyActive && !filters.status
              ? applyFilter({ onlyActive: false })
              : applyFilter({ onlyActive: true, status: '', endedToday: false })
          }
        >
          {tr.matches.list.chips.active}
          <span className="mp-chip-count">· {counts.active}</span>
        </button>

        <button
          type="button"
          className={`mp-chip${filters.endedToday ? ' active' : ''}`}
          aria-pressed={filters.endedToday}
          onClick={() =>
            filters.endedToday
              ? applyFilter({ endedToday: false })
              : applyFilter({ endedToday: true, status: '', datePreset: '', onlyActive: false })
          }
        >
          {tr.matches.list.chips.endedToday}
          <span className="mp-chip-count">· {counts.endedInPeriod}</span>
        </button>

        {selfUid && (
          <button
            type="button"
            className={`mp-chip${filters.onlyMy ? ' active' : ''}`}
            aria-pressed={filters.onlyMy}
            onClick={() => applyFilter({ onlyMy: !filters.onlyMy })}
          >
            {tr.matches.list.chips.withMe}
            <span className="mp-chip-count">· {counts.withPlayer}</span>
          </button>
        )}

        {hasActiveFilters && (
          <button type="button" className="mp-chip mp-chip--reset" onClick={resetFilters}>
            {tr.matches.list.resetFilters}
            <span className="mp-chip-x" aria-hidden="true">✕</span>
          </button>
        )}
      </div>

      {/* ── Live: новые матчи на других страницах ────────────── */}
      {newMatchesCount > 0 && (
        <button type="button" className="mp-live-banner" onClick={showNewMatches}>
          <span className="mp-live-banner-dot" aria-hidden="true" />
          {tr.matches.list.newMatches(newMatchesCount)}
        </button>
      )}

      {/* ── Table card ──────────────────────────────────────── */}
      <div className="mp-table-card">
        <table className="mp-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>{tr.matches.list.columns.type}</th>
              <th>{tr.matches.list.columns.startDuration}</th>
              {showSelfColumn && <th>{tr.matches.list.columns.myHero}</th>}
              {showOtherColumn && <th>{otherFocusHeader}</th>}
              <th>{tr.matches.list.columns.players}</th>
              <th>{tr.matches.list.columns.winner}</th>
              <th>{tr.matches.list.columns.round}</th>
              <th>{tr.matches.list.columns.status}</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {loading && content.length === 0 ? (
              <tr className="mp-state-row">
                <td colSpan={columnCount} style={{ padding: 0 }}>
                  <TableSkeleton
                    rows={10}
                    columns={skeletonColumns}
                    showHeader={false}
                    ariaLabel={tr.matches.list.loadingLabel}
                  />
                </td>
              </tr>
            ) : content.length === 0 ? (
              <tr className="mp-state-row">
                <td colSpan={columnCount}>
                  <EmptyState
                    icon="🎮"
                    title={tr.matches.list.emptyTitle}
                    description={hasActiveFilters ? tr.matches.list.emptyHint : undefined}
                    actions={hasActiveFilters ? [{ label: tr.matches.list.resetFilters, onClick: resetFilters, variant: 'outline' }] : undefined}
                  />
                </td>
              </tr>
            ) : (
              content.map(match => {
                const self = selfUid ? match.players?.find(p => p.uid === selfUid) : undefined;
                const otherFocusPlayer = showOtherColumn ? match.players?.find(p => otherFocusUids.includes(p.uid)) : undefined;
                return (
                <tr key={match.id} className={`row-link-host${self && !playerFilterActive ? ' mp-row-self' : ''}`}>
                  <td data-label="ID">
                    <Link
                      to={`/matches/${encodeURIComponent(match.id)}`}
                      state={{ match }}
                      className="row-link mp-cell-id"
                      aria-label={tr.matches.list.openMatch(match.id)}
                    >
                      {match.id}
                    </Link>
                  </td>
                  <td data-label={tr.matches.list.columns.type}>
                    <span className={`mp-type-pill${modeClass(match.matchTypeCode)}`}>{formatMatchType(match.matchTypeCode)}</span>
                  </td>
                  <td data-label={tr.matches.list.columns.startDuration}>
                    <div className="mp-cell-dt">
                      <span className="mp-cell-dt-main">{formatShortDate(match.startDateTime)}</span>
                      <span className="mp-cell-dt-sep">·</span>
                      <span className="mp-cell-dt-dur">{formatDuration(match.startDateTime, match.endDateTime)}</span>
                    </div>
                  </td>
                  {showSelfColumn && (
                    <td data-label={tr.matches.list.columns.myHero}>
                      <FocusHeroCell player={self} />
                    </td>
                  )}
                  {showOtherColumn && (
                    <td data-label={otherFocusHeader}>
                      <FocusHeroCell player={otherFocusPlayer} />
                    </td>
                  )}
                  <td data-label={tr.matches.list.columns.players}>
                    <PlayersSummary players={match.players} />
                  </td>
                  <td data-label={tr.matches.list.columns.winner}>
                    <WinnerCell players={match.players} />
                  </td>
                  <td data-label={tr.matches.list.columns.round}>
                    <RoundCell round={match.round} />
                  </td>
                  <td data-label={tr.matches.list.columns.status}>
                    <StatusPill ended={match.ended} />
                  </td>
                  <td className="mp-cell-arrow-cell">
                    <span className="mp-cell-arrow">→</span>
                  </td>
                </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* ── Pagination ──────────────────────────────────────── */}
      {totalPages > 1 && (
        <div className="mp-pagination">
          <span className="mp-pagination-info">
            {tr.matches.list.pageInfo(page + 1, totalPages, totalElements)}
          </span>
          <div className="mp-pagination-btns">
            <button
              type="button"
              className="mp-page-btn"
              disabled={page === 0}
              onClick={() => goToPage(page - 1)}
            >
              {tr.matches.list.prev}
            </button>
            <button
              type="button"
              className="mp-page-btn current"
              disabled
            >
              {page + 1}
            </button>
            <button
              type="button"
              className="mp-page-btn"
              disabled={page >= totalPages - 1}
              onClick={() => goToPage(page + 1)}
            >
              {tr.matches.list.next}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default MatchesPage;
